/**
 * Every film, with the plan type it sells and the page it plays on. A plan type may have
 * several — one story per reason somebody wants the building — and each is a page of its
 * own: a path in `site/paths.ts`, listed in its type's `filmPaths` (`site/planTypes.ts`),
 * and an HTML entry at that path in `vite.config.ts`. `test/film.test.ts` holds the four
 * to each other, so a film cannot ship without its page or a page without its film.
 *
 * A how-to is drawn with the same kit and played by the same page, so it is an entry here
 * too, of kind `how-to`: listed in its type's `howToPaths` rather than `filmPaths`, because
 * it teaches one technique for minutes and is not one of the thirty second films.
 */

import type { PlanType } from "../site/planTypes.js";
import { SHED_FILM_PATH, SHED_FLOOR_HOW_TO_PATH, SHED_WORKSHOP_FILM_PATH } from "../site/paths.js";
import type { Film } from "./film.js";
import { floorHowTo } from "./floorHowTo.js";
import { floorHowToScore } from "./floorHowToScore.js";
import { shedScore } from "./shedScore.js";
import { shedFilm } from "./sheds.js";
import { workshopFilm } from "./workshop.js";
import { workshopScore } from "./workshopScore.js";

export type FilmKind = "film" | "how-to";

export interface FilmEntry {
  type: PlanType;
  /** A thirty second film that sells the plans, or a how-to that teaches a technique. */
  kind: FilmKind;
  /** Where it plays: its page's address. */
  path: string;
  film: Film;
}

export const FILMS: readonly FilmEntry[] = [
  { type: "shed", kind: "film", path: SHED_FILM_PATH, film: { ...shedFilm, score: shedScore } },
  { type: "shed", kind: "film", path: SHED_WORKSHOP_FILM_PATH, film: { ...workshopFilm, score: workshopScore } },
  { type: "shed", kind: "how-to", path: SHED_FLOOR_HOW_TO_PATH, film: { ...floorHowTo, score: floorHowToScore } },
];

/** The film that plays at `path`, a page's address with its trailing slash. */
export function filmAt(path: string): FilmEntry | undefined {
  return FILMS.find((entry) => entry.path === path);
}
